import type { Translate } from "@similarweb/i18n-provider";
import {
    formatDate,
    shortMonthDayAndYear,
    TextCell,
    TruncatedTextCell,
    type ColumnsType,
} from "@similarweb/pro-logic-components";

import { VisibilityBadge } from "app/pages/sales-intelligence/common-components/VisibilityBadge/VisibilityBadge";
import { compareItemsByDateDesc, stringSorter } from "app/pages/sales-intelligence/utils/sort";

import type { Playbook } from "../../types/playbooks.types";

import { PlaybooksTableActionsCell } from "./PlaybooksTableActionsCell";
import { PlaybooksTableCadenceCell } from "./PlaybooksTableCadenceCell";

export type PlaybooksColumnHandlers = {
    onEdit: (playbook: Playbook) => void;
    onDelete: (playbook: Playbook) => void;
    onConnectSalesloft: () => void;
};

export type PlaybooksColumnsOptions = {
    isSalesloftReconnectRequired: boolean;
};

export const getPlaybooksColumns = (
    translate: Translate,
    handlers: PlaybooksColumnHandlers,
    { isSalesloftReconnectRequired }: PlaybooksColumnsOptions,
): ColumnsType<Playbook> => [
    {
        dataIndex: "name",
        title: translate("si.playbooks.column.playbook"),
        sorter: stringSorter("name"),
        width: 380,
        render: (_value, record) => <TruncatedTextCell>{record.name ?? ""}</TruncatedTextCell>,
    },
    {
        dataIndex: "isSharedWithAccount",
        title: translate("si.playbooks.column.type"),
        width: 110,
        render: (_value, record) => (
            <VisibilityBadge
                isShared={Boolean(record.isSharedWithAccount)}
                dataAutomationSuffix={String(record.playbookId)}
            />
        ),
    },
    {
        dataIndex: "cadence",
        title: translate("si.playbooks.column.cadence"),
        render: (_value, record: Playbook) => (
            <PlaybooksTableCadenceCell
                cadence={record.cadence}
                isSalesloftReconnectRequired={isSalesloftReconnectRequired}
                onConnectSalesloft={handlers.onConnectSalesloft}
            />
        ),
    },
    {
        dataIndex: "updatedDate",
        title: translate("si.playbooks.column.updated"),
        width: 140,
        sorter: (a, b) => compareItemsByDateDesc(a.updatedDate, b.updatedDate),
        render: (_value, record) => (
            <TextCell>{formatDate(record.updatedDate, undefined, shortMonthDayAndYear)}</TextCell>
        ),
    },
    {
        key: "actions",
        title: "",
        width: 108,
        render: (_value, record) => (
            <PlaybooksTableActionsCell
                playbook={record}
                onEdit={handlers.onEdit}
                onDelete={handlers.onDelete}
            />
        ),
    },
];
